/**
 * The breeze-sword art (id breeze-sword; display name from the game data — renamed 10-11, ids kept), the starter art, in the sample-page style (lane FX-PORT wave 1 batch A). The art's own
 * look: wind through bamboo — jade and leaf ramps with white cores, curling wind streaks, bamboo-leaf lancets, ground
 * grass bent by the gust. Each move is staged anticipation -> strike -> impact -> dissipation (the taiji page's bar).
 * Original designs (no reference expression). bs-sweep plays batch 1's approved spin sample re-tinted into the art's
 * jade and leaf (shapes and timing unchanged); bs-cloud is redrawn as wind passing through (no beams: the user, 10-11).
 */
import { add, blade, crescent, diamond, hash, isoRing, lerp, line, px, unit, type G, type V } from '../kit';
import { SAMPLES, type Scene } from '../skills';
import {
  bladeArc,
  C,
  clamp01,
  fade,
  FPS,
  hitStar,
  isoDisc,
  leaf,
  level,
  puffs,
  rampMap,
  retint,
  ringOn,
  since,
  strikeAt,
  tilesPx,
  windCurl,
  type ArtFx,
  windPass,
} from '../families/swordKit';

const up = (d: V): V => (d[0] >= 0 ? [d[1], -d[0]] : [-d[1], d[0]]);

/** An upright ring of air (a gust's punch seen side on) at `c`, growing with `e`, dithered away. */
function airBurst(g: G, c: V, e: number, life = 4, big = 1): void {
  if (e < 0 || e >= life) return;
  const rx = (3 + e * 2.2) * big;
  const ry = (6 + e * 3.2) * big;
  const n = Math.round((rx + ry) * 3);
  level(g, e < 2 ? 3 : 2);
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2;
    px(
      g,
      c[0] + Math.cos(a) * rx,
      c[1] + Math.sin(a) * ry,
      i % 4 === 0 ? C('white', 8) : C('jade', 6 - Math.min(2, e)),
    );
  }
  g.globalAlpha = 1;
}

/** Grass flattened by wind: a 2-cell slant along `d` (dark blade, lit tip). */
function bentGrass(g: G, p: V, d: V): void {
  px(g, p[0], p[1], C('leaf', 2));
  px(g, p[0] + Math.sign(d[0] || 1), p[1], C('leaf', 3));
  px(g, p[0] + Math.sign(d[0] || 1) * 2, p[1] - 1, C('leaf', 4));
}

/** Leaves cut loose at `c`: flung along `dir` (and up), turning, falling under gravity, dithered out. */
function leafBurst(g: G, c: V, e: number, n: number, dir: V, spread: number, seed: number, life = 10): void {
  if (e < 0 || e >= life) return;
  const base = Math.atan2(dir[1], dir[0]);
  for (let i = 0; i < n; i++) {
    const a = base + (hash(i, seed) - 0.5) * spread - 0.4;
    const v = 2.2 + hash(i, seed + 1) * 2.4;
    const x = c[0] + Math.cos(a) * v * e;
    const y = c[1] + Math.sin(a) * v * e * 0.8 + 0.22 * e * e;
    level(g, fade(e, life * 0.5, life * 0.8));
    leaf(g, x, y, i + e, i % 3 ? 'leaf' : 'jade', i % 2 === 0);
  }
  g.globalAlpha = 1;
}

// ── 1. bs-gust: a quick qi thrust, a gust lane bending the grass, curling wind past the foe ──

const gust: ArtFx = {
  id: 'breeze-gust',
  skill: 'bs-gust',
  th: 'bs-gust',
  on: true,
  note: 'ลมรวมเข้าปลายกระบี่ -> แทง ลมม้วนพุ่งเป็นช่อง หญ้าลู่ -> แรงลมกระแทกเป้า ใบไผ่หลุด -> ลมจางผ่านไป',
  weapon: 'sword',
  acts: [{ m: 'wxQi', t0: 0.3, t1: 1.3 }],
  hits: [0.8],
  big: 0.8,
  pre: 0.5,
  hit: 0.8,
  end: 1.75,
  afterimage: null,
  under(g, s) {
    // Anticipation: a small ring drawn on round the feet.
    if (s.t >= 0.45 && s.t < 0.9) {
      level(g, s.t < 0.75 ? 2 : 1);
      ringOn(g, s.a, 10, Math.PI * 0.5, (s.t - 0.45) / 0.3, C('jade', 5));
      g.globalAlpha = 1;
    }
    // Under the gust: grass laid flat as it passes and dust lifted off the ground (no lane, no slab).
    const e = since(s.t, 0.74);
    if (e < 0 || e > 14) return;
    const reach = Math.hypot(s.f[0] - s.a[0], s.f[1] - s.a[1]) + 24;
    const lv = fade(e, 7, 11);
    for (let i = 0; i < 9; i++) {
      const u = 10 + i * ((reach - 10) / 9);
      if (u > (e + 1) * 14) break;
      const side = (hash(i, 3) - 0.5) * 12;
      const p: V = [s.a[0] + s.d[0] * u - s.d[1] * side, s.a[1] + s.d[1] * u + s.d[0] * side * 0.5];
      level(g, lv);
      bentGrass(g, p, s.d);
      const de = e - Math.floor(u / 14);
      if (de >= 0 && de < 5 && i % 2) {
        level(g, fade(de, 2, 4));
        g.fillStyle = C('brown', 5);
        g.fillRect(Math.round(p[0] + de * 2 * s.d[0]), Math.round(p[1] - 1 - de * 0.5), de < 2 ? 3 : 2, 1);
      }
    }
    g.globalAlpha = 1;
  },
  over(g, s) {
    const { hand, tip } = s.limbs(Math.min(s.t, 0.8));
    const u = up(s.d);
    // Anticipation: three streaks drawn in from behind the body to the sword hand, a glint at the tip.
    if (s.t >= 0.4 && s.t < 0.78) {
      const k = clamp01((s.t - 0.4) / 0.32);
      for (let i = 0; i < 3; i++) {
        const from = add(add(s.a, s.d, -30 + i * 5), u, s.chest * (0.4 + i * 0.5));
        const p = lerp(from, hand, k);
        const q = lerp(from, hand, Math.max(0, k - 0.35));
        level(g, k < 0.3 ? 2 : 3);
        if (Math.hypot(p[0] - q[0], p[1] - q[1]) > 3) blade(g, q, p, 2, C('jade', 6), C('jade', 4), 0.85);
        px(g, p[0], p[1], C('white', 8));
      }
      if (s.t >= 0.6) {
        level(g, 3);
        diamond(g, tip[0], tip[1], s.t < 0.7 ? 1 : 2, C('jade', 5), C('white', 8));
      }
      g.globalAlpha = 1;
    }
    bladeArc(g, s, 0.68, 1.0, 'jade', 3);
    // The gust: curved streaks travelling from the tip through the foe's chest and out past it (wind, never a beam).
    const e = since(s.t, 0.74);
    const from = s.limbs(0.8).tip;
    const aim = unit([strikeAt(s)[0] - from[0], strikeAt(s)[1] - from[1]]);
    windPass(g, from, aim, e, {
      n: 7,
      speed: 14,
      len: 24,
      spread: 12,
      life: 11,
      ramp: 'jade',
      leaves: true,
      seed: 1,
    });
    // Impact: the air punches through the foe, leaves cut loose, a jade star.
    const h = since(s.t, 0.8);
    airBurst(g, strikeAt(s), h, 4);
    leafBurst(g, strikeAt(s, 0.8), h, 7, s.d, 1.6, 11);
    hitStar(g, s, strikeAt(s), h, 'jade', 1);
    // Dissipation: motes drifting on past the foe.
    if (h >= 2 && h < 14)
      for (let i = 0; i < 6; i++) {
        const p = add(
          add(s.f, s.d, 6 + (h - 2) * (2 + hash(i, 4) * 2)),
          u,
          s.chest * (0.3 + hash(i, 5) * 1.1),
        );
        level(g, fade(h, 7, 11));
        px(g, p[0], p[1], i % 2 ? C('white', 8) : C('jade', 6));
      }
    g.globalAlpha = 1;
  },
};

// ── 2. bs-glide: a gliding pass through the foe, a wind lane, a slit cut that splits open ──

const GLIDE = 104;
const glide: ArtFx = {
  id: 'breeze-glide',
  skill: 'bs-glide',
  th: 'bs-glide',
  on: true,
  note: 'ย่อตัว ฝุ่นใบไม้ยก -> ร่อนทะลุเป้า เงาร่างแยก ลมพัดตามทาง -> รอยกรีดแนวนอนผ่าอก แยกออกเป็นสองเส้น -> ใบไผ่หมุนรอบตัวแล้วร่วง',
  weapon: 'sword',
  acts: [{ m: 'wxThrust', t0: 0.3, t1: 1.2 }],
  hits: [0.63],
  big: 0.63,
  ghosts: [0.53, 0.57, 0.61],
  pre: 0.45,
  hit: 0.63,
  end: 1.8,
  dash: GLIDE,
  afterimage: 'dash',
  move(s, t) {
    const k0 = clamp01((t - 0.5) / 0.24);
    const k = 1 - (1 - k0) * (1 - k0);
    const back = 1 - clamp01((t - 2.0) / 0.3);
    return [s.d[0] * GLIDE * k * back, s.d[1] * GLIDE * k * back];
  },
  under(g, s) {
    // The kick-off scuff.
    puffs(g, s.a, since(s.t, 0.48), 6, 3, 2, 'brown', 6, 0.3, 2);
    // The wind lane: two dark streaks along the glide with grass laid flat, dithered away after.
    const e = since(s.t, 0.5);
    if (e < 0 || e > 18) return;
    const b = s.body(Math.min(s.t, 0.8));
    const lv = fade(since(s.t, 0.85), 6, 11);
    level(g, Math.min(2, lv));
    line(g, [s.a[0] + 4, s.a[1] + 2], [b[0] - 2, b[1] + 2], C('leaf', 2));
    line(g, [s.a[0] + 8, s.a[1] - 2], [b[0] - 6, b[1] - 2], C('leaf', 2));
    level(g, Math.min(3, lv));
    const L = Math.hypot(b[0] - s.a[0], b[1] - s.a[1]);
    for (let x = 8; x < L - 4; x += 9) bentGrass(g, add(s.a, s.d, x + (hash(x, 2) - 0.5) * 3), s.d);
    g.globalAlpha = 1;
  },
  over(g, s) {
    const u = up(s.d);
    // Anticipation: a glint at the tip held low, two leaves lifted off the ground.
    if (s.t >= 0.35 && s.t < 0.52) {
      const { tip } = s.limbs(s.t);
      level(g, 3);
      diamond(g, tip[0], tip[1], s.t > 0.44 ? 2 : 1, C('jade', 5), C('white', 8));
      for (let i = 0; i < 3; i++)
        leaf(g, s.a[0] - 6 + i * 6, s.a[1] - 2 - (s.t - 0.35) * 40 * (0.6 + i * 0.2), i + since(s.t, 0.35));
      g.globalAlpha = 1;
    }
    // The glide: two wavy ribbons at waist and knee trailing the body, curls left behind in the wake.
    const e = since(s.t, 0.52);
    const b = s.body(s.t);
    if (e >= 0 && e < 10) {
      for (let i = 0; i < 2; i++) {
        const h = s.chest * (i ? 0.25 : 0.85);
        const head = add(add(b, s.d, -5), u, h);
        // A ribbon of wind trailing the body, never longer than 40 px (wind, not a tube).
        const tail0 = add(add(s.a, s.d, -8 + Math.max(0, e - 3) * 16 + i * 6), u, h);
        const L0 = (head[0] - tail0[0]) * s.d[0] + (head[1] - tail0[1]) * s.d[1];
        const tail = L0 > 40 ? add(head, s.d, -40) : tail0;
        const L = Math.min(40, L0);
        if (L < 4) continue;
        level(g, fade(e, 5, 8));
        const n = Math.round(L);
        for (let k = 0; k <= n; k++) {
          const p = lerp(tail, head, k / n);
          const wave = Math.round(Math.sin(k * 0.22 + i * 2 - e * 0.9) * (1.5 + (k / n) * 1.5));
          const y = p[1] + wave;
          const near = k > n - 8;
          px(g, p[0], y, near ? C('white', 8) : C('jade', k > n * 0.5 ? 6 : 5));
          if (k > n * 0.35) px(g, p[0], y + 1, C('jade', 4));
        }
      }
      g.globalAlpha = 1;
    }
    for (let j = 0; j < 4; j++) {
      const at = 0.54 + j * 0.03;
      const w = since(s.t, at);
      if (w < 0 || w > 8) continue;
      const p = add(add(s.body(at), s.d, -14 - j * 4), u, s.chest * (0.4 + (j % 2) * 0.7));
      level(g, fade(w, 3, 6));
      windCurl(g, add(p, s.d, -9), s.d, 9, j % 2 ? 2 : 3, C('jade', 6), C('jade', 4));
    }
    g.globalAlpha = 1;
    bladeArc(g, s, 0.56, 0.86, 'jade', 3);
    // Impact: a rising slit across the foe's chest (a draw-cut in passing), held, then split open and dithered.
    const h = since(s.t, 0.63);
    const c = strikeAt(s, 1.1);
    if (h >= 0 && h < 9) {
      const v = unit([1, -0.42]);
      const nv: V = [-v[1], v[0]];
      const Lh = h < 3 ? 34 : 34 - (h - 3) * 3;
      const a = add(c, v, -Lh);
      const z = add(c, v, Lh);
      if (h < 2) {
        level(g, 3);
        blade(g, a, z, 3, C('white', 8), C('jade', 5), 0.5);
        line(g, add(a, nv, 2), add(z, nv, 2), C('jade', 3));
      } else {
        const gap = 1 + Math.floor((h - 2) / 2);
        level(g, fade(h, 5, 7));
        line(g, add(a, nv, -gap), add(z, nv, -gap), C('jade', 6));
        line(g, add(a, nv, gap), add(z, nv, gap), C('jade', 5));
        for (let i = 0; i < 5; i++) {
          const q = add(lerp(a, z, hash(i, h + 3)), nv, (i % 2 ? 1 : -1) * (gap + 1 + (h - 2)));
          px(g, q[0], q[1], i % 2 ? C('white', 8) : C('jade', 6));
        }
      }
      g.globalAlpha = 1;
    }
    hitStar(g, s, c, h, 'jade', 1.1);
    // Dissipation: leaves whirled round the body where the glide stopped, then let go.
    const w = since(s.t, 0.74);
    if (w >= 0 && w < 14) {
      const stop = s.body(0.8);
      for (let i = 0; i < 7; i++) {
        const a = (i / 7) * Math.PI * 2 + w * 0.7;
        const free = Math.max(0, w - 6);
        const r = 12 + free * 2.5;
        const x = stop[0] + Math.cos(a) * r + free * s.d[0] * 1.5;
        const y = stop[1] - s.chest * 0.8 + Math.sin(a) * r * 0.4 + free * free * 0.12;
        level(g, fade(w, 8, 11));
        leaf(g, x, y, i + w, i % 2 ? 'leaf' : 'jade', true);
      }
      g.globalAlpha = 1;
    }
  },
};

// ── 3. bs-bamboo: a breath circle, bamboo shoots rising round the body, the exhale pulse ──

const STALKS = 6;
const stalkAt = (s: Scene, i: number): { p: V; a: number } => {
  const a = -Math.PI / 2 + (i * Math.PI * 2) / STALKS + 0.3;
  return { p: [s.a[0] + Math.cos(a) * 27, s.a[1] + Math.sin(a) * 13.5], a };
};
const stalkH = (s: Scene, i: number): number => Math.round(s.chest * (0.8 + hash(i, 2) * 0.4));
/**
 * One bamboo shoot: two cells wide for its lower two thirds, one above, a dark node notch every 5 cells, a fan of three
 * leaves opening at its tip once grown; it sways after the exhale and dithers away at the end.
 */
function stalk(g: G, s: Scene, i: number): void {
  if (s.t < 0.3 || s.t > 2.3) return;
  const { p } = stalkAt(s, i);
  const H = stalkH(s, i);
  const h = Math.min(H, Math.floor((Math.max(0, s.t - 0.3 - i * 0.04) * 80) / 3) * 3);
  if (h < 2) return;
  const lv = s.t < 1.7 ? 3 : s.t < 2.0 ? 2 : 1;
  const sway = s.t > 0.95 ? Math.sin((s.t - 0.95) * 6 + i) * 1.6 : 0;
  level(g, lv);
  for (let y = 0; y < h; y++) {
    const lean = (stalkAt(s, i).p[0] - s.a[0]) / 27;
    const x = p[0] + Math.round(((sway + lean * 3) * y * y) / Math.max(1, H * H));
    const node = y > 1 && y % 5 === 0;
    const wide = y < H * 0.66;
    px(g, x, p[1] - y, node ? C('leaf', 2) : C('leaf', wide ? 4 : 5));
    if (wide) px(g, x + 1, p[1] - y, node ? C('leaf', 2) : C('leaf', 3));
  }
  if (h >= H) {
    const tx = p[0] + Math.round(sway + ((p[0] - s.a[0]) / 27) * 3);
    const ty = p[1] - h;
    const open = clamp01((s.t - 0.62 - i * 0.04) / 0.2);
    if (open > 0) {
      leaf(g, tx - 2, ty + 1, 3, 'leaf', true);
      if (open > 0.5) leaf(g, tx + 2, ty + 2, 1, 'jade', true);
      if (open >= 1) leaf(g, tx + 3, ty - 1, 0, 'leaf', true);
    }
  }
  g.globalAlpha = 1;
}
const behind = (s: Scene, i: number): boolean => stalkAt(s, i).p[1] < s.a[1];

const bamboo: ArtFx = {
  id: 'breeze-bamboo',
  skill: 'bs-bamboo',
  th: 'bs-bamboo',
  on: true,
  note: 'วงลมหายใจวาดรอบเท้า หน่อไผ่งอกขึ้นรอบตัว -> หายใจเข้า แสงดึงเข้าอก -> หายใจออก วงคลื่นขยาย -> ฟื้นพลัง แสงเขียวลอยขึ้น ใบไผ่ปลิว -> ไผ่จางหาย',
  weapon: 'sword',
  acts: [{ m: 'wxHeal', t0: 0.1, t1: 2.4 }],
  hits: [],
  pre: 0.1,
  hit: 0.9,
  end: 2.6,
  afterimage: null,
  under(g, s) {
    if (s.t < 0.15 || s.t > 2.4) return;
    const lv = s.t < 2.0 ? 3 : s.t < 2.2 ? 2 : 1;
    // The breath circle drawn on, an inner ring after, a tuft at each shoot's foot.
    level(g, Math.min(lv, s.t > 1.0 ? 2 : 3));
    ringOn(g, s.a, 27, -Math.PI / 2, (s.t - 0.15) / 0.35, C('jade', 5), s.t < 1.0);
    if (s.t > 0.35) ringOn(g, s.a, 19, Math.PI / 2, (s.t - 0.35) / 0.3, C('jade', 4));
    for (let i = 0; i < STALKS; i++) {
      if (s.t < 0.3 + i * 0.04) continue;
      const { p } = stalkAt(s, i);
      px(g, p[0] - 1, p[1] + 1, C('leaf', 2));
      px(g, p[0] + 2, p[1] + 1, C('leaf', 2));
    }
    g.globalAlpha = 1;
    // The exhale: two rings rolling out from the circle.
    const e = since(s.t, 0.9);
    for (let j = 0; j < 2; j++) {
      const f = e - j * 2;
      if (f < 0 || f > 6) continue;
      level(g, fade(f, 2, 4));
      isoRing(g, s.a[0], s.a[1], 28 + f * 6, C('jade', j ? 5 : 6), f < 2);
    }
    g.globalAlpha = 1;
    for (let i = 0; i < STALKS; i++) if (behind(s, i)) stalk(g, s, i);
  },
  over(g, s) {
    for (let i = 0; i < STALKS; i++) if (!behind(s, i)) stalk(g, s, i);
    const chest: V = [s.a[0], s.a[1] - s.chest];
    // Inhale: motes drawn in on a spiral from round the circle to the chest.
    if (s.t >= 0.5 && s.t < 0.92) {
      const k = (s.t - 0.5) / 0.42;
      for (let i = 0; i < 10; i++) {
        const kk = clamp01(k * 1.3 - (i % 4) * 0.1);
        const a = (i / 10) * Math.PI * 2 + kk * 2.2;
        const r = 34 * (1 - kk);
        const p: V = [
          chest[0] + Math.cos(a) * r,
          chest[1] + s.chest * 0.6 * (1 - kk) + Math.sin(a) * r * 0.45,
        ];
        if (kk >= 1) continue;
        // A mote: a lit 2x2 bead with a short tail back along its spiral.
        const back: V = [p[0] - Math.cos(a + 1.2) * 4, p[1] - Math.sin(a + 1.2) * 2];
        level(g, kk < 0.15 ? 2 : 3);
        line(g, back, p, C('jade', 4));
        g.fillStyle = C('jade', 6);
        g.fillRect(Math.round(p[0]), Math.round(p[1]), 2, 2);
        px(g, p[0], p[1], C('white', 8));
      }
      g.globalAlpha = 1;
    }
    // Exhale: a soft burst at the chest.
    const e = since(s.t, 0.9);
    if (e >= 0 && e < 3) {
      level(g, e < 2 ? 3 : 2);
      diamond(g, chest[0], chest[1], e === 0 ? 2 : 1, C('jade', 5), C('white', 8));
      for (let i = 0; i < 8; i++) {
        const a = (i * Math.PI) / 4;
        const r0 = 4 + e * 2;
        line(
          g,
          [chest[0] + Math.cos(a) * r0, chest[1] + Math.sin(a) * r0],
          [chest[0] + Math.cos(a) * (r0 + 3), chest[1] + Math.sin(a) * (r0 + 3)],
          C('jade', 6),
        );
      }
      g.globalAlpha = 1;
    }
    // The heal: small leaf-crosses rising up the body, staggered.
    if (s.t >= 1.0 && s.t < 2.55)
      for (let i = 0; i < 9; i++) {
        const born = 1.0 + i * 0.13;
        const age = s.t - born;
        if (age < 0 || age > 0.9) continue;
        const x = s.a[0] + (hash(i, 6) - 0.5) * 22;
        const y = s.a[1] - 2 - age * 46;
        const f = Math.floor(age * FPS);
        level(g, fade(f, 6, 9));
        // A small cross of light (outlined: 3 thick at its middle), a glint above it.
        g.fillStyle = C('jade', 5);
        g.fillRect(Math.round(x) - 2, Math.round(y), 5, 1);
        g.fillRect(Math.round(x), Math.round(y) - 2, 1, 5);
        g.fillRect(Math.round(x) - 1, Math.round(y) - 1, 3, 3);
        px(g, x, y, C('white', 8));
        if (f < 5) px(g, x, y - 4, C('white', 8));
      }
    // Leaves let go from the shoots' tips, drifting on the breeze.
    if (s.t >= 1.35)
      for (let i = 0; i < STALKS * 2; i++) {
        const st = stalkAt(s, i % STALKS).p;
        const born = 1.35 + (i % 4) * 0.12 + Math.floor(i / STALKS) * 0.2;
        const w = since(s.t, born);
        if (w < 0 || w > 12) continue;
        const H = stalkH(s, i % STALKS);
        const x = st[0] + w * (1.6 + hash(i, 8));
        const y = st[1] - H + w * 1.4 + Math.sin(w * 0.9 + i) * 1.5;
        level(g, fade(w, 6, 9));
        leaf(g, x, y, w + i, i % 2 ? 'leaf' : 'jade', true);
      }
    g.globalAlpha = 1;
  },
};

// ── 4. bs-mist: mist spiralling up into a wind-sealed shell round the body ──

/** The rising mist: puffs turning round the body and climbing, the back half under the figure. */
function mistSpiral(g: G, s: Scene, front: boolean): void {
  if (s.t < 0.2 || s.t > 1.2) return;
  for (let i = 0; i < 22; i++) {
    const a = (i / 22) * Math.PI * 2 + s.t * 2.6;
    if (Math.sin(a) > 0 !== front) continue;
    const upk = clamp01((s.t - 0.22 - (i % 6) * 0.05) / 0.72);
    const r = 24 - upk * 8;
    const x = Math.round(s.a[0] + Math.cos(a) * r);
    const y = Math.round(s.a[1] + Math.sin(a) * r * 0.45 - upk * s.chest * 1.9);
    // A puff: a soft 5 x 3 checker blob (no outline: mist, not a pebble), a solid lit row on top, a jade shade.
    const big = i % 3 !== 0;
    level(g, s.t > 1.05 ? 1 : 2);
    g.fillStyle = C('white', 6);
    g.fillRect(x - (big ? 2 : 1), y - 1, big ? 5 : 3, big ? 3 : 2);
    level(g, s.t > 1.05 ? 2 : 3);
    line(g, [x - 1, y - 2], [x + (big ? 1 : 0), y - 2], C('white', 8));
    px(g, x - (big ? 2 : 1), y + (big ? 1 : 0), C('jade', 5));
    level(g, 2);
    line(g, [x - Math.sin(a) * 5, y + Math.cos(a) * 2], [x - Math.sin(a) * 2, y + Math.cos(a)], C('jade', 5));
    if (i % 5 === 0) leaf(g, x + 4, y - 3, i + since(s.t, 0), 'jade', true);
  }
  g.globalAlpha = 1;
}
/**
 * The ward shell: an upright oval round the body, flashed white as it closes, then held: its near side a 2-row band
 * (white over jade), its far side a dithered line; two comet strokes of wind circle it at waist and shoulder.
 */
function shell(g: G, s: Scene, front: boolean): void {
  const e = since(s.t, 1.0);
  if (e < 0 || s.t > 2.35) return;
  const c: V = [s.a[0], s.a[1] - s.chest * 0.95];
  const rx = 16;
  const ry = Math.round(s.chest * 1.2);
  const lv = e < 2 ? 3 : s.t < 1.9 ? 3 : 1;
  const n = Math.round((rx + ry) * 4);
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2;
    const near = Math.sin(a) > 0.3;
    if (near !== front) continue;
    const x = c[0] + Math.cos(a) * rx;
    const y = c[1] + Math.sin(a) * ry;
    if (e < 2) {
      level(g, 3);
      px(g, x, y, C('white', 8));
      continue;
    }
    level(g, near ? lv : Math.min(lv, 2));
    px(g, x, y, near ? C('white', 7) : C('white', 6));
    if (near)
      px(
        g,
        c[0] + Math.cos(a) * (rx - 1),
        c[1] + Math.sin(a) * (ry - 1),
        i % 6 === 0 ? C('white', 8) : C('jade', 5),
      );
  }
  if (e >= 1 && s.t < 2.1)
    for (let j = 0; j < 2; j++) {
      const hy = c[1] + (j ? -s.chest * 0.55 : s.chest * 0.35);
      const w = j ? rx - 3 : rx;
      const a0 = s.t * (j ? -6 : 5) + j * 2;
      let prev: V | null = null;
      for (let k = 0; k < 16; k++) {
        const a = a0 - k * 0.16 * (j ? -1 : 1);
        const p: V = [c[0] + Math.cos(a) * (w + 3), hy + Math.sin(a) * (w + 3) * 0.3];
        if (prev && Math.sin(a) > 0 === front) {
          level(g, k < 6 ? 3 : k < 11 ? 2 : 1);
          line(g, prev, p, k < 3 ? C('white', 8) : C('jade', 6));
          if (k < 7) line(g, [prev[0], prev[1] + 1], [p[0], p[1] + 1], C('jade', 4));
        }
        prev = p;
      }
    }
  g.globalAlpha = 1;
}

const mist: ArtFx = {
  id: 'breeze-mist',
  skill: 'bs-mist',
  th: 'bs-mist',
  on: true,
  note: 'หมอกแผ่ใต้เท้า ไอหมอกหมุนวนไต่ขึ้นรอบตัว -> ปิดเป็นเกราะรูปไข่ แสงพุ่งที่ยอด วงยันต์ลมบนพื้น -> ลมสองสายวนรอบเกราะ -> หมอกจางกระจายออก',
  weapon: 'sword',
  acts: [{ m: 'wxBuff', t0: 0.1, t1: 2.0 }],
  hits: [],
  pre: 0.1,
  hit: 1.0,
  end: 2.5,
  afterimage: null,
  under(g, s) {
    if (s.t >= 0.2 && s.t < 2.3) {
      const r = Math.round(8 + clamp01((s.t - 0.2) / 0.4) * 14);
      isoDisc(g, s.a, r, C('white', 5), C('white', 6), s.t < 2.0 ? 1 : 0);
    }
    // The ward's ground ring with four wind notches, set on the peak.
    const e = since(s.t, 1.0);
    if (e >= 0 && s.t < 2.3) {
      level(g, e < 2 ? 3 : s.t < 2.0 ? 2 : 1);
      isoRing(g, s.a[0], s.a[1], 22, C('jade', 5), true);
      isoRing(g, s.a[0], s.a[1], 18, C('jade', 4));
      for (let i = 0; i < 4; i++) {
        const a = (i * Math.PI) / 2 + Math.PI / 4 + (s.t - 1.0) * 0.8;
        const p: V = [s.a[0] + Math.cos(a) * 20, s.a[1] + Math.sin(a) * 10];
        windCurl(g, add(p, [-4, 0]), [1, 0], 4, 2, C('white', 8), C('jade', 6));
      }
      g.globalAlpha = 1;
    }
    mistSpiral(g, s, false);
    shell(g, s, false);
  },
  over(g, s) {
    mistSpiral(g, s, true);
    shell(g, s, true);
    // The seal: two curls of wind meeting over the crown, a flash where they close.
    const e = since(s.t, 0.9);
    if (e >= 0 && e < 7) {
      const top: V = [s.a[0], s.a[1] - s.chest * 2.2];
      const k = Math.min(1, (e + 1) / 3);
      level(g, fade(e, 4, 6));
      windCurl(g, [top[0] - 16, top[1] + 6 - k * 4], [1, -0.3], 10 * k + 2, 3, C('white', 8), C('jade', 5));
      windCurl(g, [top[0] + 16, top[1] + 6 - k * 4], [-1, -0.3], 10 * k + 2, 3, C('white', 8), C('jade', 5));
      if (e >= 2 && e < 5) diamond(g, top[0], top[1] - 2, e === 2 ? 2 : 1, C('jade', 5), C('white', 8));
      g.globalAlpha = 1;
    }
    // Dissipation: the mist let go outward, a few leaves carried off.
    puffs(g, [s.a[0], s.a[1] - 4], since(s.t, 2.0), 10, 14, 3, 'white', 7, 0.6, 4);
  },
};

// ── 5. bs-reed: reeds bending toward the foe (the tell), then three crossing cuts ──

const REED_HITS = [0.75, 0.9, 1.05] as const;
/** The reeds of the wind-up: a fan of stems in front of the caster, bending toward the foe, laid flat by the cuts. */
function reeds(g: G, s: Scene, front: boolean): void {
  if (s.t < 0.1 || s.t > 1.8) return;
  const bend = clamp01((s.t - 0.1) / 0.45);
  const flat = since(s.t, REED_HITS[2]);
  const dir = Math.atan2(s.d[1] * 2, s.d[0]);
  for (let i = 0; i < 13; i++) {
    const a = dir + (i - 6) * 0.45;
    const r = 14 + (i % 3) * 7;
    const b: V = [s.a[0] + Math.cos(a) * r, s.a[1] + Math.sin(a) * r * 0.5];
    if (b[1] >= s.a[1] !== front) continue;
    const H = 12 + (i % 4) * 1.5;
    const sw = Math.sin(s.t * 9 + i) * 0.6;
    level(g, flat < 0 ? 3 : fade(flat, 6, 9));
    // A curved stem: the lean grows with height (squared), toward the foe as the wind-up fills; laid flat by the cuts.
    let prev: V = b;
    let tip: V = b;
    for (let k = 1; k <= 6; k++) {
      const u = k / 6;
      const q: V =
        flat >= 0
          ? [b[0] + s.d[0] * H * u, b[1] - 1 * u]
          : [b[0] + s.d[0] * (bend * 9 + sw) * u * u, b[1] - H * u + bend * 3 * u * u];
      line(g, prev, q, k < 4 ? C('leaf', 3) : C('leaf', 4));
      prev = q;
      tip = q;
    }
    // The plume: three cells of pale seed head, lit on top.
    const f = Math.sign(s.d[0] || 1);
    px(g, tip[0], tip[1], C('ochre', 5));
    px(g, tip[0] + f, tip[1], C('ochre', 4));
    px(g, tip[0] + f * 2, tip[1] + 1, C('ochre', 3));
    if (flat < 0) px(g, tip[0], tip[1] - 1, C('white', 7));
  }
  g.globalAlpha = 1;
}

const reed: ArtFx = {
  id: 'breeze-reed',
  skill: 'bs-reed',
  th: 'bs-reed',
  on: true,
  note: 'ตั้งท่า: ต้นอ้อรอบตัวลู่เข้าหาเป้า เส้นลมชี้ไปที่เป้า ประกายปลายกระบี่ -> ฟันสามจังหวะ ลงเฉียง ขึ้นเฉียงไขว้ กวาดแนวนอน -> แรงลมระเบิดที่เป้า ใบไผ่กระจาย -> รอยฟันค้างแล้วจาง ต้นอ้อล้มราบ',
  weapon: 'sword',
  acts: [
    { m: 'wxCleave', t0: 0.4, t1: 0.86 },
    { m: 'wxRise', t0: 0.86, t1: 1.0 },
    { m: 'wxCleave', t0: 1.0, t1: 1.5 },
  ],
  hits: [...REED_HITS],
  big: REED_HITS[2],
  cast: [0.1, 0.55],
  pre: 0.5,
  hit: REED_HITS[0],
  end: 1.95,
  afterimage: null,
  under(g, s) {
    reeds(g, s, false);
    // The tell: a line drawn on the ground to the foe, a small ring where it lands.
    if (s.t >= 0.1 && s.t < 1.2) {
      const k = clamp01((s.t - 0.1) / 0.45);
      const L = Math.hypot(s.f[0] - s.a[0], s.f[1] - s.a[1]);
      level(g, s.t < 0.75 ? 2 : 1);
      line(g, add(s.a, s.d, 8), add(s.a, s.d, 8 + k * (L - 12)), C('jade', 4));
      if (k >= 1) isoRing(g, s.f[0], s.f[1], 7, C('jade', 5), s.t < 0.75);
      g.globalAlpha = 1;
    }
  },
  over(g, s) {
    reeds(g, s, true);
    // Wind-up glint at the tip, swelling.
    if (s.t >= 0.25 && s.t < 0.62) {
      const { tip } = s.limbs(s.t);
      level(g, 3);
      diamond(g, tip[0], tip[1], s.t < 0.4 ? 1 : 2, C('jade', 5), C('white', 8));
      g.globalAlpha = 1;
    }
    bladeArc(g, s, 0.62, 1.2, 'jade', 3);
    // Three cuts across the foe: falling diagonal, rising diagonal (an X), then a wide level stroke.
    const c = strikeAt(s);
    const cuts: [V, V, number][] = [
      [[-12, -11], [11, 9], 2],
      [[-12, 9], [12, -10], 2],
      [[-20, 1], [24, -2], 3],
    ];
    cuts.forEach(([p, q, w], k) => {
      const e = since(s.t, REED_HITS[k]! - 0.04);
      if (e < 0 || s.t > 1.75) return;
      // The scene is staged left to right (a foe on the left is drawn mirrored), so the cuts are fixed shapes.
      const a = add(c, p);
      const b = add(c, q);
      const z = e === 0 ? lerp(a, b, 0.55) : b;
      level(g, e < 3 ? 3 : e < 8 ? 2 : 1);
      if (e < 3) blade(g, a, z, w, C('white', 8), C('jade', 4), 0.6);
      else line(g, a, z, C('jade', 5));
      g.globalAlpha = 1;
      leafBurst(g, c, since(s.t, REED_HITS[k]!), 3, [b[0] - a[0], b[1] - a[1]], 0.8, 20 + k, 9);
      hitStar(g, s, c, since(s.t, REED_HITS[k]!), 'jade', k === 2 ? 1.3 : 0.8);
    });
    // The last cut's gust bursts through the foe.
    const h = since(s.t, REED_HITS[2]);
    airBurst(g, c, h, 5, 1.2);
    leafBurst(g, c, h, 8, s.d, 2.6, 31, 12);
  },
};

// ── 6. bs-tempest (the art's ท่าใหญ่): a 4.4 s set piece — gather, cyclone, four dashes through the foe, climax, aftermath ──
// The user (10-11): a full rage bar buys a big, violent move with grand FX and 3-5 s of animation. Beats (sample s):
//   0.05-1.20 gather: the area ring drawn on (the tell), leaves and dust sucked in on a spiral, rings contracting to the
//             feet, wind lifting off the shoulders (robe and hair lift), a curl over the head;
//   1.20      release: the bamboo-leaf cyclone erupts round the caster, a shock ring to the area's edge (blow 1);
//   1.50 1.85 2.20 2.55  four dashes through the foe from four angles, split-body afterimages along each path, a blade
//             gust across the foe on each pass, leaves cut loose (blows 2-5);
//   3.00      climax: the leaves drawn up over the foe, then two great blade gusts in an X and a burst (blow 6);
//   3.20-4.40 aftermath: leaves raining and settling on the ground, spiral scars and dash grooves fading.

const T_R = tilesPx(3);
const T_REL = 1.2;
const T_DASH = [1.5, 1.85, 2.2, 2.55] as const;
const T_CLIMAX = 3.0;
const T_END = 4.4;
/** The dash paths' angles through the foe (screen, the iso ground squashing y). */
const T_ANG = [-0.35, 0.5, -0.95, 0.12] as const;
const TEMPEST_HITS = [T_REL, ...T_DASH, T_CLIMAX];
const dashDir = (k: number): V => [Math.cos(T_ANG[k]!), Math.sin(T_ANG[k]!) * 0.5];
const dashEnds = (s: Scene, k: number): [V, V] => {
  const d = dashDir(k);
  const f: V = [s.f[0], s.f[1]];
  return [add(f, d, -44), add(f, d, 40)];
};
/** Where the page body stands: home, then each dash from one side of the foe through to the other, then home. */
function tempestMove(s: Scene, t: number): V {
  if (t < T_DASH[0] - 0.05 || t >= T_CLIMAX - 0.15) return [0, 0];
  let k = 0;
  for (let i = 0; i < T_DASH.length; i++) if (t >= T_DASH[i]! - 0.05) k = i;
  const [p, q] = dashEnds(s, k);
  const u = clamp01((t - (T_DASH[k]! - 0.05)) / 0.16);
  const at = lerp(p, q, 1 - (1 - u) * (1 - u));
  return [at[0] - s.a[0], at[1] - s.a[1]];
}

/** The gather: leaves and motes drawn in from past the area's edge on a turning spiral, each with a tail. */
function gatherIn(g: G, s: Scene, front: boolean): void {
  if (s.t < 0.05 || s.t > T_REL + 0.05) return;
  const at = (i: number, k: number): V => {
    const a = (i / 36) * Math.PI * 2 + k * 3.6;
    const r = (T_R + 18) * (1 - k) + 8;
    return [s.a[0] + Math.cos(a) * r, s.a[1] + Math.sin(a) * r * 0.5 - k * s.chest * 1.3];
  };
  for (let i = 0; i < 36; i++) {
    const k = clamp01((s.t - 0.05 - (i % 9) * 0.09) / 0.55);
    if (k <= 0 || k >= 1) continue;
    const a = (i / 36) * Math.PI * 2 + k * 3.6;
    if (Math.sin(a) > 0 !== front) continue;
    const p = at(i, k);
    const q = at(i, Math.max(0, k - 0.12));
    level(g, k < 0.1 ? 2 : 3);
    line(g, q, p, C('jade', k < 0.5 ? 4 : 5));
    if (i % 4 === 0) px(g, p[0], p[1], C('white', 8));
    else if (i % 4 === 1) {
      g.fillStyle = C('brown', 5);
      g.fillRect(Math.round(p[0]), Math.round(p[1]), 2, 1);
    } else leaf(g, p[0], p[1], i + Math.floor(k * 8), i % 2 ? 'leaf' : 'jade', true);
  }
  g.globalAlpha = 1;
}
/** Wind through the gather: comet rings climbing the body, and lift strokes rising off the shoulders (robe, hair). */
function windColumn(g: G, s: Scene, front: boolean): void {
  if (s.t < 0.3 || s.t > T_REL + 0.05) return;
  for (let j = 0; j < 4; j++) {
    const u = ((s.t - 0.3 + j * 0.2) % 0.55) / 0.55;
    const h = u * s.chest * 2.3;
    const rx = 16 - u * 6;
    const phi = s.t * 13 + j * 2;
    let prev: V | null = null;
    for (let i = 0; i <= 12; i++) {
      const a = phi - i * 0.2;
      const p: V = [s.a[0] + Math.cos(a) * rx, s.a[1] - h + Math.sin(a) * rx * 0.32];
      if (prev && Math.sin(a) > 0 === front) {
        level(g, i < 5 ? 3 : i < 9 ? 2 : 1);
        line(g, prev, p, i < 2 ? C('white', 8) : C('jade', 5));
      }
      prev = p;
    }
  }
  if (front && s.t > 0.55)
    for (let i = 0; i < 6; i++) {
      const u = ((s.t * 2.2 + i * 0.17) % 1) * 14;
      const x = s.a[0] - 9 + i * 3.6;
      const y = s.a[1] - s.chest * 1.7 - u;
      level(g, u < 7 ? 3 : 2);
      line(g, [x, y + 4], [x + (i % 2 ? 1 : -1), y], i % 2 ? C('white', 7) : C('jade', 6));
    }
  g.globalAlpha = 1;
}
/** The cyclone: three stacked turning bands (wider up the funnel), three comet strokes each, leaves riding them. */
function cyclone(g: G, s: Scene, front: boolean): void {
  const e = since(s.t, T_REL - 0.05);
  if (e < 0 || s.t > T_CLIMAX + 0.6) return;
  // Dust whipped round the cyclone's foot.
  if (s.t < T_CLIMAX + 0.2)
    for (let i = 0; i < 14; i++) {
      const a = (i / 14) * Math.PI * 2 + s.t * 7;
      if (Math.sin(a) > 0 !== front) continue;
      const r = T_R * (0.35 + (i % 3) * 0.15) * clamp01((e + 1) / 5);
      // Dithered and pale (a dark 2-row blob would get an outline and read as a hole).
      level(g, 2);
      g.fillStyle = C('brown', i % 3 ? 6 : 5);
      g.fillRect(Math.round(s.a[0] + Math.cos(a) * r), Math.round(s.a[1] + Math.sin(a) * r * 0.5 - 1), 3, 2);
    }
  const grow = clamp01((e + 1) / 5);
  const out = Math.max(0, since(s.t, T_CLIMAX + 0.05));
  const lv = out < 2 ? 3 : out < 5 ? 2 : 1;
  for (let j = 0; j < 3; j++) {
    const h = [3, s.chest * 0.95, s.chest * 1.85][j]! + out * (j + 1) * 1.5;
    const rx = (14 + (T_R * 0.78 - 14) * grow) * [0.6, 0.85, 1.1][j]! + out * 3;
    const c: V = [s.a[0], s.a[1] - h];
    for (let m = 0; m < 3; m++) {
      const phi = s.t * (9 - j * 1.5) + (m * Math.PI * 2) / 3 + j * 0.7;
      let prev: V | null = null;
      const N = 18;
      for (let i = 0; i <= N; i++) {
        const a = phi - i * (2.4 / N);
        const p: V = [c[0] + Math.cos(a) * rx, c[1] + Math.sin(a) * rx * 0.3];
        if (prev && Math.sin(a) > 0 === front) {
          level(g, Math.min(lv, i < 6 ? 3 : i < 11 ? 2 : 1));
          line(g, prev, p, i < 3 ? C('white', 8) : C('jade', i < 9 ? 5 : 4));
          if (i < 7) line(g, [prev[0], prev[1] + 1], [p[0], p[1] + 1], C('jade', 3));
          if (i % 4 === 1 && out < 4) leaf(g, p[0], p[1] - 3, e + j + m + i, m % 2 ? 'leaf' : 'jade', true);
        }
        prev = p;
      }
    }
  }
  g.globalAlpha = 1;
}
/** Leaves lying on the ground after the storm (fixed spots in the area), dithered away by the end. */
function settled(g: G, s: Scene): void {
  if (s.t < 3.3 || s.t > T_END) return;
  for (let i = 0; i < 30; i++) {
    const born = 3.3 + hash(i, 44) * 0.6;
    if (s.t < born) continue;
    const a = hash(i, 40) * Math.PI * 2;
    const r = 8 + hash(i, 41) * (T_R - 6);
    level(g, s.t < 4.05 ? 3 : s.t < 4.25 ? 2 : 1);
    leaf(
      g,
      s.a[0] + Math.cos(a) * r,
      s.a[1] + Math.sin(a) * r * 0.5,
      i % 2 ? 0 : 1,
      i % 3 ? 'leaf' : 'jade',
      true,
    );
  }
  g.globalAlpha = 1;
}

const tempest: ArtFx = {
  id: 'breeze-tempest',
  skill: 'bs-tempest',
  th: 'bs-tempest',
  note: 'ท่าใหญ่ 4.4 วิ: รวมพลัง 1.2 วิ (วงพื้นที่ วงแหวนหดเข้าเท้า ใบไผ่และฝุ่นถูกดูดวน ลมยกชายเสื้อและผม) -> ปล่อย: พายุใบไผ่ระเบิดรอบตัว -> พุ่งทะลุเป้าสี่ครั้งจากสี่มุม ร่างเงาแยกตามทาง ลมคมดาบผ่าเป้าทุกครั้ง -> ไคลแมกซ์: ใบไผ่ถูกดึงขึ้นเหนือเป้า แล้วลมดาบไขว้ X ระเบิด -> ใบไม้โปรยลงนอนบนพื้น รอยเกลียวและร่องพุ่งค่อยจาง',
  weapon: 'sword',
  acts: [
    { m: 'wxPower', t0: 0.05, t1: 1.3 },
    { m: 'wxThrust', t0: 1.45, t1: 1.7 },
    { m: 'wxCleave', t0: 1.8, t1: 2.05 },
    { m: 'wxRise', t0: 2.15, t1: 2.4 },
    { m: 'wxThrust', t0: 2.5, t1: 2.75 },
    { m: 'wxBreak', t0: 2.8, t1: 3.6 },
  ],
  hits: [...TEMPEST_HITS],
  big: T_CLIMAX,
  cast: [0.05, T_REL - 0.1],
  pre: T_REL - 0.3,
  hit: T_REL,
  end: T_END,
  ghosts: T_DASH.flatMap((t) => [t + 0.02, t + 0.06, t + 0.1]),
  stepIn: 8,
  afterimage: 'port',
  move: tempestMove,
  beats: [
    { t: 0.05, th: 'รวมพลัง' },
    { t: T_REL, th: 'ปล่อยพายุ' },
    { t: T_DASH[0], th: 'พุ่ง 1' },
    { t: T_DASH[1], th: 'พุ่ง 2' },
    { t: T_DASH[2], th: 'พุ่ง 3' },
    { t: T_DASH[3], th: 'พุ่ง 4' },
    { t: T_CLIMAX, th: 'ไคลแมกซ์' },
    { t: 3.3, th: 'หลังพายุ' },
  ],
  under(g, s) {
    // The tell: the area ring drawn on through the gather, rings contracting to the feet, spokes spiralling in.
    if (s.t >= 0.05 && s.t < 3.7) {
      const k = clamp01((s.t - 0.05) / 0.9);
      level(g, s.t < 3.3 ? 3 : 2);
      ringOn(g, s.a, T_R, -Math.PI / 2, k, C('jade', 5), k >= 1);
      if (k >= 1) {
        level(g, 1);
        isoRing(g, s.a[0], s.a[1], T_R - 4, C('leaf', 3));
      }
      g.globalAlpha = 1;
    }
    if (s.t >= 0.3 && s.t < T_REL) {
      for (let j = 0; j < 3; j++) {
        const u = ((s.t - 0.3) * 1.6 + j / 3) % 1;
        level(g, u < 0.6 ? 2 : 3);
        isoRing(g, s.a[0], s.a[1], 10 + (T_R - 14) * (1 - u), C(j % 2 ? 'leaf' : 'jade', 4));
      }
      for (let i = 0; i < 8; i++)
        for (let q = 0; q < 10; q++) {
          const u = q / 10;
          const a = (i * Math.PI) / 4 + u * 1.3 + s.t * 1.8;
          const r = T_R - 4 - u * (T_R - 14);
          level(g, 2);
          px(g, s.a[0] + Math.cos(a) * r, s.a[1] + Math.sin(a) * r * 0.5, C('jade', 4));
        }
      g.globalAlpha = 1;
    }
    // Release and climax: shock rings to the edge, the area flashed.
    [T_REL, T_CLIMAX].forEach((h, j) => {
      const e = since(s.t, h);
      if (e < 0 || e > 7) return;
      for (let q = 0; q <= j; q++) {
        const f = e - q * 2;
        if (f < 0) continue;
        const r = 12 + (T_R + j * 14 - 12) * Math.min(1, (f + 1) / 4);
        level(g, fade(f, 3, 5));
        isoRing(g, s.a[0], s.a[1], r, C('jade', 6), f < 3);
        isoRing(g, s.a[0], s.a[1], r - 3, C('white', 8));
      }
      g.globalAlpha = 1;
      if (e < 4) isoDisc(g, s.a, T_R, C('jade', 3), null, e < 2 ? 1 : 0);
    });
    // Each dash leaves a groove along its path through the foe; the climax leaves spiral scars.
    T_DASH.forEach((h, k) => {
      if (s.t < h || s.t > 4.1) return;
      const [p, q] = dashEnds(s, k);
      level(g, s.t < 3.4 ? 2 : 1);
      line(g, [p[0], p[1] + 2], [q[0], q[1] + 2], C('leaf', 2));
      line(g, [p[0] + 3, p[1] + 3], [q[0] - 3, q[1] + 3], C('brown', 3));
    });
    if (s.t >= T_CLIMAX + 0.1 && s.t < T_END)
      for (let i = 0; i < 8; i++)
        for (let q = 0; q < 9; q++) {
          const u = q / 9;
          const a = (i * Math.PI) / 4 + 0.4 + u * 1.0;
          const r = 14 + u * (T_R - 18);
          level(g, s.t < 3.8 ? 3 : s.t < 4.1 ? 2 : 1);
          px(g, s.a[0] + Math.cos(a) * r, s.a[1] + Math.sin(a) * r * 0.5, C('leaf', 2));
          px(g, s.a[0] + Math.cos(a) * r + 1, s.a[1] + Math.sin(a) * r * 0.5 + 1, C('brown', 3));
        }
    g.globalAlpha = 1;
    settled(g, s);
    gatherIn(g, s, false);
    windColumn(g, s, false);
    cyclone(g, s, false);
  },
  over(g, s) {
    gatherIn(g, s, true);
    windColumn(g, s, true);
    // The build: tip glint pulsing faster, a curl over the head growing.
    if (s.t >= 0.4 && s.t < T_REL) {
      const { tip } = s.limbs(s.t);
      const f = since(s.t, 0);
      level(g, 3);
      diamond(g, tip[0], tip[1], s.t > 0.9 ? (f % 2 ? 3 : 2) : f % 3 ? 1 : 2, C('jade', 5), C('white', 8));
      const k = clamp01((s.t - 0.4) / 0.6);
      windCurl(
        g,
        [s.a[0] - 4 - k * 6, s.a[1] - s.chest * 2.4],
        [1, 0],
        6 + k * 8,
        2 + Math.round(k * 2),
        C('white', 8),
        C('jade', 5),
      );
      g.globalAlpha = 1;
    }
    cyclone(g, s, true);
    // The release blow.
    const e0 = since(s.t, T_REL);
    airBurst(g, strikeAt(s), e0, 5, 1.3);
    hitStar(g, s, strikeAt(s), e0, 'jade', 1.2);
    // Four dashes through the foe: a blade gust across it along the path, wind passing on, leaves cut loose.
    T_DASH.forEach((h, k) => {
      const e = since(s.t, h);
      if (e < -1 || e > 12) return;
      const d = dashDir(k);
      const c = strikeAt(s);
      const [p] = dashEnds(s, k);
      if (e >= 0 && e < 6) {
        const a = add(c, d, -30);
        const z = add(c, d, 34);
        level(g, e < 2 ? 3 : e < 4 ? 2 : 1);
        if (e < 3) blade(g, a, e === 0 ? c : z, 4, C('white', 8), C('jade', 5), 0.55);
        // A crescent of wind wrapping the foe the way the pass went.
        if (e < 4)
          crescent(
            g,
            c[0],
            c[1],
            14 + e * 2,
            T_ANG[k]! - 1.1,
            T_ANG[k]! + 1.1,
            3,
            C('white', 8),
            C('jade', 4),
            0.6,
            true,
          );
        else line(g, a, z, C('jade', 5));
        g.globalAlpha = 1;
      }
      windPass(g, add(p, [0, -s.chest]), d, e + 1, {
        n: 5,
        speed: 16,
        len: 18,
        spread: 14,
        life: 8,
        ramp: 'jade',
        leaves: true,
        seed: 10 + k,
      });
      leafBurst(g, c, e, 5, d, 1.4, 50 + k, 9);
      airBurst(g, c, e, 4, 0.9);
      hitStar(g, s, c, e, 'jade', 1);
    });
    // Climax: leaves drawn up over the foe, then two great blade gusts in an X and a burst.
    const pre = s.t - (T_CLIMAX - 0.35);
    if (pre >= 0 && pre < 0.35)
      for (let i = 0; i < 16; i++) {
        const a = (i / 16) * Math.PI * 2 + pre * 9;
        const r = 26 * (1 - pre / 0.35) + 4;
        const x = s.f[0] + Math.cos(a) * r;
        const y = s.f[1] - s.chest * 0.5 - (pre / 0.35) * s.chest * 1.6 + Math.sin(a) * r * 0.3;
        level(g, 3);
        leaf(g, x, y, i + since(s.t, 0), i % 2 ? 'leaf' : 'jade', true);
      }
    const ec = since(s.t, T_CLIMAX);
    if (ec >= 0 && ec < 7) {
      const c = strikeAt(s, 0.9);
      level(g, ec < 2 ? 3 : ec < 4 ? 2 : 1);
      blade(g, add(c, [-30, -26]), add(c, [30, 24]), 4, C('white', 8), C('jade', 5), 0.5);
      blade(g, add(c, [-30, 24]), add(c, [30, -26]), 4, C('white', 8), C('jade', 5), 0.5);
      g.globalAlpha = 1;
    }
    airBurst(g, strikeAt(s), ec, 6, 2);
    airBurst(g, strikeAt(s), ec - 2, 6, 1.4);
    hitStar(g, s, strikeAt(s), ec, 'jade', 1.8);
    leafBurst(g, strikeAt(s, 1.4), ec, 16, [0, -1], 5.5, 77, 13);
    // Aftermath: leaves raining over the whole area, turning, settling.
    const w = since(s.t, T_CLIMAX + 0.25);
    if (w >= 0 && w < 16)
      for (let i = 0; i < 26; i++) {
        const a = hash(i, 40) * Math.PI * 2;
        const r = 8 + hash(i, 41) * (T_R - 6);
        const gx = s.a[0] + Math.cos(a) * r;
        const gy = s.a[1] + Math.sin(a) * r * 0.5;
        const h = Math.max(0, s.chest * 2.6 - w * (2.6 + hash(i, 42) * 2));
        if (h <= 0) continue;
        level(g, fade(w, 11, 14));
        leaf(g, gx + Math.sin(w * 0.8 + i) * 2, gy - h, w + i, i % 2 ? 'leaf' : 'jade', true);
      }
    g.globalAlpha = 1;
  },
};

// ── 7. bs-sweep: batch 1's spin sample in the art's colours; 8. bs-cloud: a new design (the comet was a beam) ──

/** An approved sample re-tinted by `pairs` (from ramp -> to ramp), with the game timing batch 1 gave it. */
function tinted(
  id: string,
  skill: string,
  pairs: Record<string, string>,
  t: Pick<ArtFx, 'pre' | 'hit' | 'end' | 'cast' | 'ghosts' | 'stepIn' | 'afterimage' | 'note'>,
): ArtFx {
  const src = SAMPLES.find((x) => x.id === id)!;
  const map = rampMap(pairs);
  const fx: ArtFx = {
    ...src,
    ...t,
    on: true,
    id: `breeze-${id}`,
    skill,
    th: skill,
    under: src.under ? (g, s) => src.under!(retint(g, map), { ...s, sample: src }) : undefined,
    over: (g, s) => src.over(retint(g, map), { ...s, sample: src }),
  };
  return fx;
}

/**
 * bs-cloud (a break): wind driven THROUGH the guard, no beam. Wind-up: grass and dust drawn in round the feet in a
 * spiral, an amber-edged ring drawn on (the break tell), three leaves circling the tip. Thrust: a short blade-path arc,
 * then a gust of curved streaks with leaves that travels through the foe and out past it; the foe's robe and hair
 * flicked the wind's way, two pressure ripples, dust kicked off the ground behind it. Then the streaks fly on and thin,
 * leaves drift down, the dust settles.
 */
const CLOUD_HIT = 0.8;
const cloud: ArtFx = {
  id: 'breeze-cloud',
  skill: 'bs-cloud',
  th: 'bs-cloud',
  on: true,
  note: 'ตั้งท่า: หญ้าและฝุ่นถูกดูดวนเข้าหาเท้า วงขอบอำพันวาดรอบตัว (สัญญาณท่าทำลาย) ใบไผ่สามใบวนที่ปลายกระบี่ -> แทง: สายลมโค้งพาใบไผ่พุ่งทะลุผ่านเป้าออกไปด้านหลัง (ไม่มีลำแสง) -> เสื้อและผมเป้าสะบัดตามลม แรงกดอากาศสองระลอก ฝุ่นฟุ้งด้านหลัง -> ลมพัดผ่านไป ใบไม้ร่วง ฝุ่นจาง',
  weapon: 'sword',
  acts: [{ m: 'wxThrust', t0: 0.1, t1: 1.3 }],
  hits: [CLOUD_HIT],
  big: CLOUD_HIT,
  cast: [0.2, 0.7],
  pre: 0.45,
  hit: CLOUD_HIT,
  end: 1.85,
  afterimage: null,
  under(g, s) {
    // The tell: grass bits spiralling in to the feet, an amber-edged ring drawn on round them.
    if (s.t >= 0.2 && s.t < 0.85) {
      const k = clamp01((s.t - 0.2) / 0.5);
      for (let i = 0; i < 14; i++) {
        const kk = clamp01(k * 1.4 - (i % 5) * 0.08);
        if (kk >= 1) continue;
        const a = (i / 14) * Math.PI * 2 + kk * 2.4;
        const r = 30 - kk * 22;
        const p: V = [s.a[0] + Math.cos(a) * r, s.a[1] + Math.sin(a) * r * 0.5];
        level(g, kk < 0.15 ? 2 : 3);
        line(g, p, [p[0] - Math.sin(a) * 3, p[1] + Math.cos(a) * 1.5], i % 3 ? C('leaf', 4) : C('brown', 5));
      }
      level(g, 3);
      ringOn(g, s.a, 14, Math.PI * 0.5, k, C('ochre', 4), k >= 1);
      level(g, 2);
      ringOn(g, s.a, 11, Math.PI * 0.5, k, C('jade', 5));
      g.globalAlpha = 1;
    }
    // Dust kicked off the ground behind the foe, blown the wind's way.
    const e = since(s.t, CLOUD_HIT);
    if (e >= 0 && e < 10)
      for (let i = 0; i < 9; i++) {
        const x = s.f[0] + 4 + e * (2 + hash(i, 61) * 2.5) + (hash(i, 62) - 0.5) * 8;
        const y = s.f[1] + (hash(i, 63) - 0.5) * 6 - e * 0.4;
        level(g, fade(e, 4, 7));
        g.fillStyle = C('brown', e < 4 ? 5 : 4);
        g.fillRect(Math.round(x) - 1, Math.round(y), e < 5 ? 3 : 2, e < 5 ? 2 : 1);
      }
    g.globalAlpha = 1;
  },
  over(g, s) {
    const { tip } = s.limbs(Math.min(s.t, CLOUD_HIT));
    // Wind-up: three leaves circling the tip, the glint swelling.
    if (s.t >= 0.3 && s.t < 0.78) {
      const w = since(s.t, 0.3);
      for (let i = 0; i < 3; i++) {
        const a = w * 0.9 + (i * Math.PI * 2) / 3;
        level(g, 3);
        leaf(g, tip[0] + Math.cos(a) * 6, tip[1] + Math.sin(a) * 4, w + i, i ? 'leaf' : 'jade', true);
      }
      diamond(g, tip[0], tip[1], s.t < 0.55 ? 1 : 2, C('jade', 5), C('white', 8));
      g.globalAlpha = 1;
    }
    bladeArc(g, s, 0.66, 0.95, 'jade', 3);
    // The gust through the foe and out past it.
    const from = s.limbs(CLOUD_HIT - 0.05).tip;
    const aim: V = [strikeAt(s)[0] - from[0], strikeAt(s)[1] - from[1]];
    windPass(g, from, aim, since(s.t, 0.74), {
      n: 9,
      speed: 15,
      len: 22,
      spread: 16,
      life: 10,
      ramp: 'jade',
      leaves: true,
      seed: 3,
    });
    const e = since(s.t, CLOUD_HIT);
    const c = strikeAt(s);
    // The foe's robe and hair flicked the wind's way: short streaming strands off its back edge.
    if (e >= 0 && e < 6)
      for (let i = 0; i < 5; i++) {
        const y = s.f[1] - s.chest * (0.3 + i * 0.35);
        const x = s.f[0] + 4;
        const L = 5 + (e < 3 ? e * 2 : 6 - (e - 3)) + (i % 2) * 2;
        const flick = Math.sin(e * 1.4 + i) * 1.5;
        level(g, fade(e, 3, 5));
        line(g, [x, y], [x + L, y + flick - 1], i % 2 ? C('white', 7) : C('jade', 5));
      }
    // Two pressure ripples at the chest.
    airBurst(g, c, e, 5, 1.2);
    airBurst(g, add(c, [6, 0]), e - 2, 5, 0.9);
    hitStar(g, s, c, e, 'jade', 1.3);
    // Leaves let go of the wind and drift down past the foe.
    leafBurst(g, add(c, [10, 0]), e - 3, 6, [1, -0.2], 1.0, 71, 12);
  },
};
const sweep = tinted(
  'spin',
  'bs-sweep',
  { gold: 'jade', fire: 'leaf' },
  {
    note: 'แถบหยกกวาดรอบตัวหนึ่งรอบ ใบไผ่ปลิวออกจากแนวฟัน ประกายจากหัวแถบ -> วงขยายจาง (แบบตัวอย่าง 1 เปลี่ยนสีเป็นหยก)',
    pre: 0.5,
    hit: 0.85,
    end: 1.9,
    ghosts: [0.6, 0.68, 0.76],
    stepIn: 6,
    afterimage: 'port',
  },
);

/** The breeze-sword moves in the sample style (all off in the game until main's review). */
export const BREEZE_FX: readonly ArtFx[] = [gust, cloud, sweep, glide, bamboo, mist, reed, tempest];
